import React, { useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";

import { useForm } from "../useForm";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import AddProductToOrderForm from "../orders/modals/AddProductToOrderForm";
import SunDistributionDetailsSection from "./SunDistributionDetailsSection";
import SunOrderProductsTable from "./SunOrderProductsTable";
import { orderStatusOptions } from "../../../shared/utility";

const GOZDE_EMPLOYEE = { value: 253, label: "Gozde" };

const today = () => new Date();

/** Defaults for fields removed from the UI — still sent on create/update. */
const REMOVED_FIELD_DEFAULTS = {
  notes: "",
  sun_customer: null,
  sun_customer_po_number: "",
  sun_bol_number: "",
  pallet_count: "",
  chep_pallet_count: "",
  total_freight_weight: "",
  appointment_number: "",
  temperature_recorder_number: "",
  carrier_name: "",
  is_prepaid: false,
  invoice_number: "",
  sun_quickbooks_invoice_id: null,
  seal_number: "",
  wfp_text: "",
  wfp_load_id_number: "",
  wfp_pickup_date: "",
  wfp_gate_in_gate_out_times: "",
};

const initialFValues = {
  id: 0,
  customer: "",
  customer_id: 0,
  created_by: GOZDE_EMPLOYEE,
  subtotal_dollar_amount: 0,
  total_dollar_amount: 0,
  order_date: today(),
  expected_date: today(),
  po_number: "",
  products: [],
  ...REMOVED_FIELD_DEFAULTS,
};

export default function SunOrderForm(props: any) {
  const {
    customers,
    getMyProducts,
    isEdit,
    order,
    setEmptyModelOpen,
    showErrorModal,
    updateOrderApiCall,
    createOrderApiCall,
    onUpdateOrderItem,
    deleteOrderItemApiCall,
    addProductToOrderApiCall,
    updateOrderItemUnitPrice,
    openSunShippingLabelReview,
    openSunBolReview,
    createSunOrderInvoice,
  } = props;

  const asyncRef = useRef<any>(null);
  const [asyncSelectValue, setAsyncSelectValue] = useState({ value: -1, label: "" });

  const validate = (fieldValues = values) => {
    const temp: any = { ...errors };

    const customer = fieldValues.customer ?? values.customer;
    temp.customer = null;
    if (customer === "" || customer == null) {
      temp.customer = "Please fill out field.";
    }

    temp.created_by = null;

    temp.po_number = null;
    if (!(fieldValues.po_number ?? values.po_number)) {
      temp.po_number = "Please fill out field.";
    }

    const orderDate = fieldValues.order_date ?? values.order_date;
    temp.order_date = null;
    if (!orderDate) {
      temp.order_date = "Please fill out field.";
    }

    temp.expected_date = null;

    temp.products = null;
    if ((fieldValues.products ?? values.products).length < 1) {
      temp.products = "Please add at least one product.";
    }

    setErrors(temp);
    return Object.values(temp).every((x) => x === null);
  };

  const {
    values,
    setValues,
    errors,
    setErrors,
    handleInputChange: formHandleInputChange,
  }: any = useForm(
    initialFValues,
    false,
    validate
  );

  const handleInputChange = (e: any) => {
    if (e?.target?.name === "order_date") {
      setValues((current: any) => ({
        ...current,
        order_date: e.target.value,
        expected_date: e.target.value,
        created_by: GOZDE_EMPLOYEE,
      }));
      return;
    }

    formHandleInputChange(e);
  };

  useEffect(() => {
    asyncRef.current?.focus();
  }, []);

  useEffect(() => {
    if (order) {
      const orderDate = order.order_date ?? today();
      setValues({
        ...order,
        status: orderStatusOptions.filter((option) => option.value === order.status)[0],
        is_prepaid: order.is_prepaid === "1" || order.is_prepaid === true,
        created_by: GOZDE_EMPLOYEE,
        expected_date: order.expected_date ?? orderDate,
      });
    }
  }, [order, setValues]);

  const findIndexForProduct = (productId: number) =>
    values.products.findIndex((element: any) => element.product_id === productId);

  const recalculateTotals = (products: any[]) => {
    const subtotal = products.reduce((sum, item) => {
      const total = parseFloat(item.total_dollar_amount ?? 0);
      return sum + (Number.isNaN(total) ? 0 : total);
    }, 0);

    return {
      subtotal_dollar_amount: subtotal.toFixed(2),
      total_dollar_amount: subtotal.toFixed(2),
    };
  };

  const addProductToOrder = async (addedProductValues: any) => {
    setAsyncSelectValue({ value: -1, label: "" });
    const quantity = parseFloat(addedProductValues.quantity ?? 0);
    const unitPrice = parseFloat(addedProductValues.unit_price ?? 0);
    const safeQuantity = Number.isNaN(quantity) ? 0 : quantity;
    const safeUnitPrice = Number.isNaN(unitPrice) ? 0 : unitPrice;
    const productToAdd = {
      id: -1,
      product_name: addedProductValues.name_en,
      product_id: addedProductValues.id,
      sun_product_id: addedProductValues.id,
      quantity: addedProductValues.quantity,
      unit_of_measure: addedProductValues.unit_of_measure,
      packing_size: addedProductValues.weight,
      individual_unit_size: addedProductValues.individual_unit_size,
      selling_unit_of_measure: addedProductValues.selling_unit_of_measure,
      plu: addedProductValues.plu,
      unit_price: safeUnitPrice,
      total_dollar_amount: (safeQuantity * safeUnitPrice).toFixed(2),
      sscc_hri_number: addedProductValues.sscc_hri_number ?? "",
    };

    if (findIndexForProduct(productToAdd.product_id) > -1) {
      showErrorModal("Product already in order, please edit it.");
      return;
    }

    let callResult: any = { success: false };
    let newProducts = [];

    if (isEdit) {
      callResult = await addProductToOrderApiCall(values.id, productToAdd);
      newProducts = [...values.products, callResult.data];
    } else {
      newProducts = [...values.products, productToAdd];
    }

    if (callResult.success || !isEdit) {
      setValues({
        ...values,
        products: newProducts,
        ...recalculateTotals(newProducts),
      });
      setEmptyModelOpen({ childComponent: null, open: false });
      setErrors((currentErrors: any) => ({ ...currentErrors, products: null }));
    }
  };

  const withCalculatedTotal = (item: any) => {
    const quantity = parseFloat(item.quantity ?? 0);
    const unitPrice = parseFloat(item.unit_price ?? 0);
    if (Number.isNaN(quantity) || Number.isNaN(unitPrice)) {
      return item;
    }
    return {
      ...item,
      total_dollar_amount: (quantity * unitPrice).toFixed(2),
    };
  };

  const updateItemLocally = (newData: any, index: number) => {
    const currentProducts = [...values.products];
    currentProducts[index] = newData;
    setValues({
      ...values,
      products: currentProducts,
      ...recalculateTotals(currentProducts),
    });
  };

  const onProductFieldChange = (
    index: number,
    field: "sscc_hri_number" | "quantity" | "unit_price",
    value: string
  ) => {
    const currentProducts = [...values.products];
    const currentItem = currentProducts[index];
    if (!currentItem) return;

    let nextItem = {
      ...currentItem,
      [field]: value,
    };

    if (field === "quantity" || field === "unit_price") {
      nextItem = withCalculatedTotal(nextItem);
    }

    currentProducts[index] = nextItem;
    setValues({
      ...values,
      products: currentProducts,
      ...recalculateTotals(currentProducts),
    });
  };

  const onItemEditComplete = async (newData: any, index: number) => {
    const nextData = withCalculatedTotal(newData);

    if (isEdit) {
      const result = await onUpdateOrderItem(nextData.id, {
        quantity: nextData.quantity,
        sscc_hri_number: nextData.sscc_hri_number ?? "",
      });
      if (result.success) {
        updateItemLocally(nextData, index);
      }
      return;
    }

    updateItemLocally(nextData, index);
  };

  const onUnitPriceEditComplete = async (newData: any, index: number) => {
    const nextData = withCalculatedTotal(newData);

    if (isEdit) {
      const result = await updateOrderItemUnitPrice(
        nextData.id,
        nextData.unit_price ?? 0
      );
      if (result.success) {
        updateItemLocally(nextData, index);
      }
      return;
    }

    updateItemLocally(nextData, index);
  };

  const makeDeleteOrderItemApiCall = async (itemIdInDB: number, productId: number) => {
    let onItemDeleted: any = { success: false };
    if (isEdit) {
      onItemDeleted = await deleteOrderItemApiCall(itemIdInDB);
    }

    if (onItemDeleted.success || !isEdit) {
      const filteredItems = values.products.filter(
        (item: any) => item.product_id !== productId
      );
      setValues({
        ...values,
        products: filteredItems,
        ...recalculateTotals(filteredItems),
      });
    }

    setEmptyModelOpen({ childComponent: null, open: false });
  };

  const askDeleteOrderItem = (product: any) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() =>
            makeDeleteOrderItemApiCall(product.id, product.product_id)
          }
          message={`Are you sure you want to delete ${product.product_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const loadOptions = async (input: string) => {
    return getMyProducts(input);
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = {
      ...values,
      ...REMOVED_FIELD_DEFAULTS,
      // Keep server-managed invoice fields if already set
      invoice_number: values.invoice_number ?? "",
      sun_quickbooks_invoice_id: values.sun_quickbooks_invoice_id ?? null,
      created_by: GOZDE_EMPLOYEE,
      expected_date: values.expected_date ?? values.order_date,
    };
    if (payload.id > 0) {
      updateOrderApiCall(payload);
    } else {
      createOrderApiCall(payload);
    }
  };

  if (!isEdit) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 sm:p-5 lg:col-span-2">
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Products
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Search and add products to this order
              </p>
            </div>

            <AsyncSelect
              cacheOptions
              loadOptions={loadOptions}
              defaultOptions
              options={[]}
              className="w-full"
              ref={asyncRef}
              placeholder="Search products…"
              onChange={(e: any) => {
                setEmptyModelOpen({
                  childComponent: (
                    <AddProductToOrderForm
                      product={e}
                      confirmAction={addProductToOrder}
                      showUnitPrice
                      isEdit={false}
                      setOpen={() => {
                        setEmptyModelOpen({ open: false });
                        setAsyncSelectValue({ value: -1, label: "" });
                      }}
                    />
                  ),
                  open: true,
                });
              }}
              value={asyncSelectValue}
            />

            {errors.products ? (
              <p className="font-medium text-red-500">{errors.products}</p>
            ) : null}

            <SunOrderProductsTable
              loading={false}
              values={values}
              onProductFieldChange={onProductFieldChange}
              onItemEditComplete={onItemEditComplete}
              onUnitPriceEditComplete={onUnitPriceEditComplete}
              askDeleteOrderItem={askDeleteOrderItem}
            />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
            <SunDistributionDetailsSection
              values={values}
              errors={errors}
              handleInputChange={handleInputChange}
              customers={customers}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
            onClick={handleSubmit}
          >
            Create Sun Order
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-3 h-full">
      <div className="col-span-2">
        <div className="card-custom p-3 py-5">
          <div className="flex rounded-md">
            <AsyncSelect
              cacheOptions
              loadOptions={loadOptions}
              defaultOptions
              options={[]}
              className="w-full"
              ref={asyncRef}
              onChange={(e: any) => {
                setEmptyModelOpen({
                  childComponent: (
                    <AddProductToOrderForm
                      product={e}
                      confirmAction={addProductToOrder}
                      showUnitPrice
                      isEdit={false}
                      setOpen={() => {
                        setEmptyModelOpen({ open: false });
                        setAsyncSelectValue({ value: -1, label: "" });
                      }}
                    />
                  ),
                  open: true,
                });
              }}
              value={asyncSelectValue}
            />
          </div>

          <SunOrderProductsTable
            loading={false}
            values={values}
            onProductFieldChange={onProductFieldChange}
            onItemEditComplete={onItemEditComplete}
            onUnitPriceEditComplete={onUnitPriceEditComplete}
            askDeleteOrderItem={askDeleteOrderItem}
            showLabelAction={Boolean(values.id > 0)}
            openSunShippingLabelReview={openSunShippingLabelReview}
          />
        </div>
      </div>

      <div className="col-span-1 space-y-5">
        <div className="card-custom p-3 space-y-3">
          <SunDistributionDetailsSection
            values={values}
            errors={errors}
            handleInputChange={handleInputChange}
            customers={customers}
          />

          <div className="mt-3 flex flex-wrap gap-2 py-4">
            <button
              type="submit"
              className="inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={handleSubmit}
            >
              Save
            </button>

            <button
              type="button"
              className="inline-flex justify-center rounded-md bg-slate-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700"
              onClick={() => openSunBolReview?.(values)}
            >
              BOL
            </button>

            {values.sun_quickbooks_invoice_id ? (
              <a
                href={`https://qbo.intuit.com/app/invoice?txnId=${values.sun_quickbooks_invoice_id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
              >
                View Invoice
              </a>
            ) : (
              <button
                type="button"
                className="inline-flex justify-center rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                onClick={async () => {
                  const result = await createSunOrderInvoice?.(values.id);
                  if (result?.success) {
                    setValues({
                      ...values,
                      invoice_number:
                        result.data?.invoice_number ?? values.invoice_number,
                      sun_quickbooks_invoice_id:
                        result.data?.sun_quickbooks_invoice_id ??
                        values.sun_quickbooks_invoice_id,
                    });
                  }
                }}
              >
                Create Invoice
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
